import { useState } from "react";
import { Tag, Handshake, Coffee, Star, DollarSign, ArrowRight, ArrowLeftRight, Plus } from "lucide-react";
import { EVENT_TYPES, getEventColor, getWeekKey, getMonthKey } from "@/lib/chartUtils";
import { createEvent } from "@/lib/api";

const ICONS = { tag: Tag, referral: Handshake, oneOnOne: Coffee, guest: Star, biz: DollarSign };

// Defined OUTSIDE so React treats them as stable types (prevents focus loss on re-render)
function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div className="flex-1 min-w-[140px]">
      <label className="block text-[10px] font-bold text-zinc-400 tracking-widest uppercase mb-1.5">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2.5 text-sm font-body border border-zinc-200 rounded-md bg-white text-zinc-950 focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 outline-none cursor-pointer"
      >
        <option value="">— select —</option>
        {options.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder, testId, className = "" }) {
  return (
    <div className={`flex-1 min-w-[140px] ${className}`}>
      <label className="block text-[10px] font-bold text-zinc-400 tracking-widest uppercase mb-1.5">{label}</label>
      <input
        data-testid={testId}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 text-sm font-body border border-zinc-200 rounded-md bg-white text-zinc-950 focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 outline-none"
      />
    </div>
  );
}

export default function EntryForm({ members: rawMembers, onEventCreated }) {
  const members = [...rawMembers].sort((a, b) => a.localeCompare(b));
  const [mode, setMode] = useState("tag");
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [amt, setAmt] = useState("");
  const [gName, setGName] = useState("");
  const [gPhone, setGPhone] = useState("");
  const [gEmail, setGEmail] = useState("");
  const [gBiz, setGBiz] = useState("");
  const [refNotes, setRefNotes] = useState("");
  const [refPhone, setRefPhone] = useState("");
  const [refEmail, setRefEmail] = useState("");
  const [refBiz, setRefBiz] = useState("");
  const [refName, setRefName] = useState("");
  const [bizNotes, setBizNotes] = useState("");
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const flash = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3500); };
  const reset = () => { setA(""); setB(""); setAmt(""); setGName(""); setGPhone(""); setGEmail(""); setGBiz(""); setRefNotes(""); setRefPhone(""); setRefEmail(""); setRefBiz(""); setRefName(""); setBizNotes(""); };

  const color = getEventColor(mode);

  async function submit() {
    const now = new Date();
    const wk = getWeekKey(now);
    const mk = getMonthKey(now);
    const base = { weekKey: wk, monthKey: mk, ts: now.toISOString() };

    try {
      setLoading(true);
      if (mode === "tag") {
        if (!a || !b) return flash("Pick who tagged who", false);
        if (a === b) return flash("Can't tag yourself!", false);
        await createEvent({ ...base, type: "tag", giver: a, receiver: b });
        flash(`${b} received a tag from ${a}`);
      } else if (mode === "referral") {
        if (!a || !b) return flash("Pick both people", false);
        await createEvent({ ...base, type: "referral", giver: a, receiver: b, notes: refNotes, refPhone: refPhone || undefined, refEmail: refEmail || undefined, refBiz: refBiz || undefined, refName: refName || undefined });
        flash(`${a} gave a referral to ${b}`);
      } else if (mode === "oneOnOne") {
        if (!a || !b) return flash("Pick both people", false);
        if (a === b) return flash("Can't 1-on-1 yourself!", false);
        await createEvent({ ...base, type: "oneOnOne", a, b });
        flash(`${a} & ${b} — 1-on-1 logged`);
      } else if (mode === "guest") {
        if (!a) return flash("Who brought the guest?", false);
        await createEvent({ ...base, type: "guest", bringer: a, guestName: gName || "Guest", guestPhone: gPhone, guestEmail: gEmail, guestBiz: gBiz, visitStatus: "visited", visitDate: new Date().toISOString().slice(0, 10) });
        flash(`${a} brought ${gName || "a guest"}`);
      } else if (mode === "biz") {
        if (!a || !b || !amt) return flash("Fill in all fields", false);
        const n = parseInt(amt.replace(/[$,\s]/g, ""));
        if (!n || n <= 0) return flash("Enter a valid dollar amount", false);
        await createEvent({ ...base, type: "biz", sender: a, receiver: b, amt: n, notes: bizNotes || undefined });
        flash(`${b} closed $${n.toLocaleString()} — sent by ${a}`);
      }
      reset();
      onEventCreated();
    } catch (err) {
      flash("Error saving — try again", false);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="entry-form" className="bg-white rounded-lg border border-zinc-200 p-5">
      <h3 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase mb-4">Log an Activity</h3>

      {/* Mode selector */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="mode-selector">
        {EVENT_TYPES.map(m => {
          const Icon = ICONS[m.key];
          const active = mode === m.key;
          const c = getEventColor(m.key);
          return (
            <button
              key={m.key}
              data-testid={`mode-${m.key}`}
              onClick={() => { setMode(m.key); reset(); }}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold transition-all border ${
                active ? `${c.btn} text-white border-transparent` : `bg-white ${c.text} ${c.border} hover:${c.bg}`
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Tag fields */}
      {mode === "tag" && (
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <SelectField label="Tagged by" value={a} onChange={setA} options={members} testId="tag-giver-select" />
          <div className="pb-2.5 text-zinc-300"><ArrowRight className="w-5 h-5" /></div>
          <SelectField label="Receiver — gets +1 credit" value={b} onChange={setB} options={members} testId="tag-receiver-select" />
        </div>
      )}

      {/* Referral fields */}
      {mode === "referral" && (
        <>
          <div className="flex flex-wrap gap-3 items-end mb-3">
            <SelectField label="Given by — gets +1 credit" value={a} onChange={setA} options={members} testId="ref-giver-select" />
            <div className="pb-2.5 text-zinc-300"><ArrowRight className="w-5 h-5" /></div>
            <SelectField label="Referred to" value={b} onChange={setB} options={members} testId="ref-receiver-select" />
          </div>
          <div className="flex flex-wrap gap-3 mb-3">
            <TextInput label="Lead's name" value={refName} onChange={setRefName} placeholder="Lead's full name..." testId="ref-name-input" />
            <TextInput label="Lead's phone" value={refPhone} onChange={setRefPhone} placeholder="555-000-0000" testId="ref-phone-input" />
          </div>
          <div className="flex flex-wrap gap-3 mb-3">
            <TextInput label="Lead's email" value={refEmail} onChange={setRefEmail} placeholder="email@example.com" testId="ref-email-input" />
            <TextInput label="Lead's business" value={refBiz} onChange={setRefBiz} placeholder="Company name..." testId="ref-biz-input" />
          </div>
          <div className="mb-4">
            <label className="block text-[10px] font-bold text-zinc-400 tracking-widest uppercase mb-1.5">Notes (optional)</label>
            <textarea
              data-testid="ref-notes-input"
              value={refNotes}
              onChange={e => setRefNotes(e.target.value)}
              placeholder="Any context about this referral..."
              className="w-full px-3 py-2.5 text-sm font-body border border-zinc-200 rounded-md bg-white text-zinc-950 focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 outline-none min-h-[60px] resize-y"
            />
          </div>
        </>
      )}

      {/* 1-on-1 fields */}
      {mode === "oneOnOne" && (
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <SelectField label="Person 1 — both get credit" value={a} onChange={setA} options={members} testId="oo-person1-select" />
          <div className="pb-2.5 text-zinc-300"><ArrowLeftRight className="w-5 h-5" /></div>
          <SelectField label="Person 2" value={b} onChange={setB} options={members} testId="oo-person2-select" />
        </div>
      )}

      {/* Guest fields */}
      {mode === "guest" && (
        <>
          <div className="flex flex-wrap gap-3 items-end mb-3">
            <SelectField label="Brought by — gets credit" value={a} onChange={setA} options={[...members, "Unassigned"]} testId="guest-bringer-select" />
            <TextInput label="Guest name" value={gName} onChange={setGName} placeholder="Guest's full name..." testId="guest-name-input" />
          </div>
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <TextInput label="Business (optional)" value={gBiz} onChange={setGBiz} placeholder="Company..." testId="guest-biz-input" />
            <TextInput label="Phone (optional)" value={gPhone} onChange={setGPhone} placeholder="555-000-0000" testId="guest-phone-input" />
            <TextInput label="Email (optional)" value={gEmail} onChange={setGEmail} placeholder="email@example.com" testId="guest-email-input" />
          </div>
        </>
      )}

      {/* Closed biz fields */}
      {mode === "biz" && (
        <div className="space-y-3 mb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <SelectField label="Sent by" value={a} onChange={setA} options={members} testId="biz-sender-select" />
            <div className="pb-2.5 text-zinc-300"><ArrowRight className="w-5 h-5" /></div>
            <SelectField label="Received by — gets $$ credit" value={b} onChange={setB} options={members} testId="biz-receiver-select" />
            <TextInput label="Amount" value={amt} onChange={setAmt} placeholder="$8,000" testId="biz-amount-input" className="!min-w-[100px] !flex-none w-[130px]" />
          </div>
          <TextInput label="Description (optional)" value={bizNotes} onChange={setBizNotes} placeholder="What was the closed business for?" testId="biz-notes-input" className="!min-w-full" />
        </div>
      )}

      <button
        data-testid="submit-entry-btn"
        onClick={submit}
        disabled={loading}
        className={`w-full py-3 rounded-md text-sm font-extrabold text-white ${color.btn} transition-all flex items-center justify-center gap-2 disabled:opacity-60`}
      >
        <Plus className="w-4 h-4" />
        LOG {mode === "tag" ? "SOCIAL TAG" : mode === "referral" ? "REFERRAL" : mode === "oneOnOne" ? "1-ON-1" : mode === "guest" ? "GUEST" : "CLOSED BUSINESS"}
      </button>

      {toast && (
        <div
          data-testid="entry-toast"
          className={`mt-3 px-4 py-2.5 rounded-md text-sm font-semibold border ${
            toast.ok ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
